import React, { useEffect, useRef, useState } from 'react';
import AuthHeader from '../../components/header/AuthHeader';
import BannerRight from '../../components/BannerRight';
import AuthWrapper from '../../components/AuthWrapper';
import rocketBoyImg from '../../assets/images/barbfullLogo.png';
import ButtonAuth from '../../components/ButtonAuth';
import OtpInput from 'react-otp-input';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTrackingContext } from '../../context/trackingContext';
import useClient from '../../hooks/useClient';
import Alert from '../../components/Alert';
import config from '../../config';
import Footer from '../../components/Footer';
import Cookies from 'js-cookie';
import Input from '../../components/Input';

const VerifyEmail = () => {
  useEffect(() => {
    trackPageView({
      pageName: 'Account created page visit',
      accountCreationStep: 3.1
    });
  }, []);

  const [otp, setOtp] = useState('');
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, isValid }
  } = useForm();

  const navigate = useNavigate();
  const client = useClient();
  const [showAlert, setShowAlert] = useState(false);

  const {
    trackFormSubmit,
    trackPageView,
    email,
    age,
    setAccountCreated,
    setEmail,
    trackError,
    internal
  } = useTrackingContext();

  const onSubmit = async (data) => {
    trackFormSubmit({
      formName: 'Verify code submit',
      accountCreationStep: 3.2
    });

    try {
      const res = await client.put(`${config.everywhereApi}/users/self/profile/verify-email`, {
        emailCode: otp,
        password: data.password
      });

      Cookies.set('_barbToken', res.data.accessToken, { expires: 1 });

      setAccountCreated('decline');
      navigate('/account');
    } catch (err) {
      const error = err?.response?.data?.error;

      trackError({ error_text: error?.errorMessage });

      setShowAlert({
        text: error ? error?.errorMessage + ' - ' + error.errorCode : 'Unknown error',
        success: false
      });
    }
  };

  const getNewCode = async () => {
    try {
      trackFormSubmit({ formName: 'Email resend request' });

      const res = await client.put(`${config.everywhereApi}/users/self/profile/generate-code`, {
        type: 'email'
      });

      setEmail(res.data.data.email);

      setShowAlert({ success: true, text: '' });
    } catch {
      trackError({ error_text: 'Unable to send new code' });

      setShowAlert({ text: 'Unable to send new code', success: false });
    }
  };

  const submitRef = useRef(null);

  useEffect(() => {
    if (otp.length === 6) {
      submitRef.current.focus();
    }
  }, [otp]);

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="w-full lg:w-1/2 relative overflow-y-scroll left-form">
        <AuthHeader />
        <AuthWrapper className="max-w-sm">
          <h1 className="font-Korolev-Bold text-5xl xl:text-5xl uppercase mb-6">
            Account <br /> Created
          </h1>

          <img src={rocketBoyImg} alt="build rocket boy" className="w-full mb-8" />

          <p className="text-black font-Korolev-Medium text-base mb-2">
            Email Verification Required.
          </p>

          <p className="text-black font-Korolev-Light text-base mb-8">
            Please enter the verification code that has been sent to your email address to complete
            account creation. If you do not see the email in your inbox, please check your junk or
            spam mail folder.
          </p>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-8 otp">
              <OtpInput
                numInputs={6}
                value={otp}
                onChange={setOtp}
                shouldAutoFocus
                inputType="tel"
                renderInput={(props) => (
                  <input
                    {...props}
                    className="border border-gray-light !w-[50px] h-[60px] text-black focus:border focus:border-black focus-visible:outline-none"
                  />
                )}
                onPaste={(event) => {
                  const pastedData = event.clipboardData.getData('text');
                  const numericData = pastedData.replace(/[^0-9]/g, '');
                  setOtp(numericData.slice(0, 6));
                }}
              />
            </div>

            {internal && process.env.REACT_APP_STAGE === 'production' && (
              <div className="mb-8">
                <p className="text-black font-Korolev-Light text-base mb-8">
                  Password required when verifying internal accounts
                </p>

                <Controller
                  control={control}
                  name="password"
                  defaultValue={''}
                  rules={{
                    required: true
                  }}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <Input
                      type="password"
                      placeholder="PASSWORD"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      className="w-full tracking-widest"
                      questionIcon={false}
                      passwordVisiblity
                    />
                  )}
                />
              </div>
            )}

            <hr className="solid mb-7 text-gray-light"></hr>

            <ButtonAuth
              isSubmitting={isSubmitting}
              isDisabled={!isValid || otp?.length < 6}
              label={'submit'}
              submitRef={submitRef}
              className="mb-5"
            />
            <ButtonAuth
              type="button"
              onClick={() => {
                getNewCode();
              }}
              label={'SEND NEW VERIFICATION CODE'}
              className="mb-5 "
            />
            <p className="text-black font-Korolev-Light text-base mb-5">
              Verification code valid for 20 minutes.
            </p>
          </form>
          {showAlert && (
            <Alert setShowAlert={setShowAlert} success={showAlert.success}>
              {showAlert.success ? (
                <p className="pr-12 text-sm md:text-base">
                  Your new code has been sent to {email} <br /> and will be valid for the next 20
                  minutes.
                </p>
              ) : (
                <p className="pr-12 text-sm md:text-base">Verification failed: {showAlert.text}</p>
              )}
            </Alert>
          )}
        </AuthWrapper>
        <Footer />
      </div>
      <BannerRight />
    </div>
  );
};

export default VerifyEmail;
